// External libraries
import EmojiPicker, { Theme } from 'emoji-picker-react';
import { Box, Typography } from '@mui/material';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';

// Components
import { OTooltip } from '@src/shared';
import AttachFile from './AttachFile';
import MessageBubble from '../MessageBubble';

// Hooks
import useChatBox from './useChatBox';

// React-icons
import { IoIosSend } from 'react-icons/io';
import { GrAttachment } from 'react-icons/gr';
import { MdErrorOutline } from 'react-icons/md';
import { IoCheckmarkSharp, IoCloseSharp } from 'react-icons/io5';

// Assets
import SpeechIcon from '@src/assets/icons/SpeechIcon';

// Constants
import { RESTRICTION_CHAT } from './chatBox.constants';

import './chatBox.scss';

const ChatBox = () => {
  const {
    theme,
    isMobileDevice,
    isMobileAndTabletDevice,

    handleDeleteMessage,
    handlePinMessage,
    handleEmojiClick,
    handleSendMessage,
    isEmojiPickerVisible,
    isSpeechActive,
    fileInputRef,
    selectedFile,
    errorMessage,
    setErrorMessage,
    isPublicChat,
    typingStatus,
    selectedReceiverData,
    handleAttachFile,
    openFileDialog,
    isDialogOpen,
    handleRemoveFile,
    message,
    messages,
    setIsEmojiPickerVisible,
    setMessage,
    toggleSpeech,
    messageContainerRef,
    emitTypingIndicator,

    isChatEmojiButtonEnabled,
    isChatFileSharingButtonEnabled,
    isChatSpeechStartButtonEnabled,

    canSaveEdit,

    removeExistingAttachment,
    cancelEdit,
    setEditingMessageId,
    setRemoveExistingAttachment,

    acceptTypes,
    isEditing,
    existingFileForChip,
    handleDeleteFileAttachment,
    isSending,
  } = useChatBox();

  return (
    <div className="chat-box-container">
      <div className="container-chat" ref={messageContainerRef}>
        {messages?.map((msg) => (
          <MessageBubble
            message={msg}
            key={`message-${msg.chatMessageId}`}
            handleDeleteMessage={handleDeleteMessage}
            handlePinMessage={handlePinMessage}
            handleDeleteFileAttachment={handleDeleteFileAttachment}
          />
        ))}
      </div>

      <div className="chat-box-footer">
        <div className="chat-box-footer-content">
          <p
            className={`typing-indicator ${
              !isPublicChat && !!typingStatus.get(selectedReceiverData.value) ? '' : 'hidden-typing'
            }`}
          >
            {typingStatus.get(selectedReceiverData.value) || ''}....
          </p>

          <div className="send-message-box">
            <TextareaAutosize
              id="chat-composer-textarea"
              className="form-input message-textarea"
              placeholder={isEditing ? 'Edit your message…' : `Send ${isPublicChat ? 'public' : 'private'} message`}
              value={message}
              maxLength={RESTRICTION_CHAT.messageLength}
              maxRows={3}
              minRows={1}
              spellCheck={false}
              onChange={(e) => {
                const value = e.target.value;
                setMessage(value);
                if (errorMessage) setErrorMessage(null);
                value.trim().length > 0 && emitTypingIndicator();
              }}
              onKeyDown={(e) => {
                if (!isMobileDevice) {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(); // saves edit when in editing mode
                  }
                  if (e.key === 'Escape' && isEditing) {
                    e.preventDefault();
                    setEditingMessageId(null);
                  }
                }
              }}
              style={{
                marginBottom: selectedFile ? '10px' : '',
                backgroundColor: 'var(--public-chat-send-message-box-bg)',
                color: 'var(--font-color)',
              }}
            />

            {selectedFile ? (
              <AttachFile selectedFile={selectedFile} fileType={selectedFile.type} onDeleteClick={handleRemoveFile} />
            ) : (
              // show the existing attachment chip during edit until the user deletes it
              isEditing &&
              existingFileForChip &&
              !removeExistingAttachment && (
                <AttachFile
                  selectedFile={existingFileForChip}
                  fileType={existingFileForChip.type}
                  onDeleteClick={() => setRemoveExistingAttachment(true)}
                />
              )
            )}
          </div>

          {errorMessage && (
            <Box display="flex">
              <MdErrorOutline style={{ color: '#d32f2f' }} />
              <Typography variant="caption" color="error" sx={{ ml: 0.5 }}>
                {errorMessage}
              </Typography>
            </Box>
          )}

          <div className="message-footer-control">
            <div className="message-boxes">
              {isChatEmojiButtonEnabled && !isMobileAndTabletDevice && (
                <OTooltip title="Insert an emoji">
                  <div
                    className={`icon-button ${isEmojiPickerVisible ? 'active' : ''}`}
                    onClick={() => setIsEmojiPickerVisible((visible) => !visible)}
                  >
                    <SentimentSatisfiedAltIcon className={'mui-icon'} style={{ fontSize: '24px' }} />
                  </div>
                </OTooltip>
              )}
              {isEmojiPickerVisible && (
                <ClickAwayListener onClickAway={() => setIsEmojiPickerVisible(false)}>
                  <div className="emoje-container">
                    <EmojiPicker
                      onEmojiClick={handleEmojiClick}
                      theme={theme === 'light' ? Theme.LIGHT : Theme.DARK}
                      skinTonesDisabled={true}
                    />
                  </div>
                </ClickAwayListener>
              )}

              {isChatSpeechStartButtonEnabled && !isMobileAndTabletDevice && (
                <OTooltip title={'Speech to text'}>
                  <div className={`icon-button speech-icon ${isSpeechActive ? ' active' : ''}`} onClick={toggleSpeech}>
                    <SpeechIcon />
                  </div>
                </OTooltip>
              )}

              {/* Disable attach while editing */}
              {isChatFileSharingButtonEnabled && (
                <OTooltip title="Attach file">
                  <div
                    className={`${isDialogOpen ? 'attachment-active-bg-color' : 'icon-button'}`}
                    onClick={openFileDialog}
                  >
                    <GrAttachment className={`mui-icon ${isDialogOpen ? 'attachment-active-color' : ''}`} />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleAttachFile}
                      accept={acceptTypes}
                      style={{ display: 'none' }}
                    />
                  </div>
                </OTooltip>
              )}
            </div>

            {/* Right-side actions */}
            {isEditing ? (
              <div className="edit-actions-container">
                <OTooltip title="Cancel">
                  <div className="icon-button cancel-button" onClick={cancelEdit} aria-label="Cancel editing">
                    <IoCloseSharp className="mui-icon" />
                  </div>
                </OTooltip>

                <OTooltip title="Save edit">
                  <div
                    className={`icon-button confirm-button ${canSaveEdit && !isSending ? 'active' : 'disabled'}`}
                    onClick={canSaveEdit && !isSending ? handleSendMessage : () => {}}
                    aria-disabled={!canSaveEdit || isSending}
                    aria-label="Save edit"
                  >
                    <IoCheckmarkSharp className="mui-icon" />
                  </div>
                </OTooltip>
              </div>
            ) : (
              <OTooltip title="Send message">
                <div
                  className={`icon-button ${(message.length > 0 || selectedFile) && !isSending ? 'active' : ''}`}
                  onClick={!isSending ? handleSendMessage : () => {}}
                >
                  <IoIosSend className={'mui-icon'} style={{ fontSize: '30px' }} />
                </div>
              </OTooltip>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatBox;
